import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ManufactureDetail } from '../gql';
import type {
    GetManufactureDetailsQuery,
    GetManufactureDetailsQueryVariables,
} from '../gql/__generated__/bom-bff';

import { useQuery } from '@apollo/client/react';

import { GetBomManufactureDetailsDocument } from '../gql';
import { GraphClientNames } from '../types/GQLClientNames';
import { isDefined } from '../utils/filter-util';

import { ManufactureDetailsContext } from './ExtendedManufactureDetailsProvider';

interface BomManufactureDetailsProviderProps {
    materialSubcategoryPublicId: string | undefined;
    skip: boolean;
    onError: (error: Error, code: string) => void;
}

const PAGE_SIZE = 200;

const ManufactureDetailsProvider = ({
    materialSubcategoryPublicId,
    skip,
    children,
    onError: handleError,
}: PropsWithChildren<BomManufactureDetailsProviderProps>): ReactElement => {
    const [generalQuery, setGeneralQuery] = useState('');

    const { data, error, loading, fetchMore } = useQuery<
        GetManufactureDetailsQuery,
        GetManufactureDetailsQueryVariables
    >(GetBomManufactureDetailsDocument, {
        variables: {
            materialSubcategoryPublicId: materialSubcategoryPublicId ?? '',
            generalQuery,
            isDeleted: false,
            pagination: {
                first: PAGE_SIZE,
            },
        },
        skip: skip || !materialSubcategoryPublicId,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        context: {
            clientName: GraphClientNames['bestone-bi4-buying-bom-bff'],
        },
    });

    const manufactureDetails = (
        data?.manufactureDetails?.edges?.map((edge) => edge?.node) || []
    ).filter(isDefined) as Array<ManufactureDetail>;

    const fetchMoreManufactureDetails = useCallback(() => {
        if (data?.manufactureDetails?.pageInfo?.hasNextPage) {
            void fetchMore({
                variables: {
                    pagination: {
                        first: PAGE_SIZE,
                        after: data.manufactureDetails.pageInfo.endCursor,
                    },
                    generalQuery,
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
                    manufactureDetails: {
                        totalCount:
                            previousQueryResult.manufactureDetails
                                ?.totalCount ?? 0,
                        edges: [
                            ...(previousQueryResult.manufactureDetails?.edges ??
                                []),
                            ...(fetchMoreResult.manufactureDetails?.edges ??
                                []),
                        ],
                        pageInfo: fetchMoreResult.manufactureDetails?.pageInfo,
                    },
                }),
            });
        }
    }, [fetchMore, generalQuery, data?.manufactureDetails?.pageInfo]);

    const contextValue = useMemo(
        () => ({
            manufactureDetails,
            isLoading: loading,
            fetchMore: fetchMoreManufactureDetails,
            generalQuery,
            setGeneralQuery,
        }),
        [manufactureDetails, loading, generalQuery, fetchMoreManufactureDetails]
    );

    useEffect(() => {
        if (error) {
            handleError(error, 'queryManufactureDetailsFailed');
        }
    }, [error, handleError]);

    return (
        <ManufactureDetailsContext.Provider value={contextValue}>
            {children}
        </ManufactureDetailsContext.Provider>
    );
};

export default ManufactureDetailsProvider;
export type { BomManufactureDetailsProviderProps };
